import { AppError } from '@/utils/api';
import { RECAPTCHA_ERROR_CODE, RECAPTCHA_ERROR_MESSAGE } from '@/services/auth';
import { AuthenticatedHttp, generateCSRF, GuestHttp } from '../../http/axios';

export const updateUserService = async ({ picture, ...info }) =>
  AuthenticatedHttp.put('/user/', {
    ...info,
    cpf: info.cpf?.replace(/\D/g, ''),
    cep: info.cep?.replace(/\D/g, ''),
  })
    .then(({ data: { success } }) => success)
    .catch((error) => {
      const appError = new AppError(error);
      const [firstFieldError] = appError.fieldErrors || [];

      throw new Error(firstFieldError?.errors?.[0] || appError.message || 'Erro ao atualizar usuário');
    });

export const updateAmbientService = async ({ viewPageAs }) => {
  try {
    const { data } = await AuthenticatedHttp.put('/user/', {
      viewPageAs,
    });

    if (!data.success) {
      throw new Error('Erro ao atualizar usuário');
    }

    return data;
  } catch (error) {
    throw new Error(error?.response?.data?.message || 'Erro ao atualizar usuário');
  }
};
export const updatePictureService = async ({ file }) => {
  const data = new FormData();
  data.append('picture', file);
  return AuthenticatedHttp.put('/user/picture/', data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
    .then(({ data: success }) => success)
    .catch((error) => {
      throw new AppError(error);
    });
};

export const updatePasswordService = async ({ oldPassword, newPassword }) =>
  AuthenticatedHttp.post('user/change-password/', {
    oldPassword,
    newPassword,
  }).then(({ data: { success } }) => {
    if (!success) {
      throw new Error('Erro ao atualizar senha');
    }
    return success;
  });

export const createPassword = async ({ password, accessToken }) => {
  try {
    if (accessToken) {
      const csrfToken = await generateCSRF();
      await AuthenticatedHttp.post(
        '/user/create-password/',
        { password },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...(csrfToken && { 'X-CSRFToken': csrfToken }),
          },
        }
      );
    } else {
      await AuthenticatedHttp.post('/user/create-password/', { password }, { withCredentials: true });
    }

    return password;
  } catch (error) {
    throw new AppError(error);
  }
};

const requestValidationToken = async (url) => {
  await generateCSRF({ forceRefresh: true });
  return AuthenticatedHttp.post(url);
};

export const requestUserEmailToken = async () =>
  requestValidationToken('/user/validation/email/request_token/');

export const requestUserWhatsappToken = async () =>
  requestValidationToken('/user/validation/whatsapp/request_token/');

export const validateEmail = async (token) =>
  AuthenticatedHttp.post('/user/validation/email/validate/', { token });

const EMAIL_ALREADY_VALIDATED_DETAIL = 'E-mail já validado.';

// O backend ainda não expõe um código estruturado para esta resposta idempotente.
export const isEmailAlreadyValidatedError = (error) =>
  error?.response?.status === 400 && error?.response?.data?.detail === EMAIL_ALREADY_VALIDATED_DETAIL;

export const validateWhatsapp = async (token) =>
  AuthenticatedHttp.post('/user/validation/whatsapp/validate/', { token });

export const sendUserNPS = async ({ note, justification }) =>
  AuthenticatedHttp.post('/user/nps/', {
    note,
    justification: justification?.length ? justification : undefined,
  }).then(({ data }) => data);

export const skipNPS = async () =>
  AuthenticatedHttp.post('/user/nps/skip/').then(({ data }) => data);

export const requestLoginLinkEmail = async (email, recaptchaToken) =>
  GuestHttp.post('/user/request_login_link_email/', { email, recaptchaToken })
    .then((response) => response.data)
    .catch((error) => {
      if (error?.response?.data?.detail === RECAPTCHA_ERROR_CODE) {
        throw new AppError({ message: RECAPTCHA_ERROR_MESSAGE });
      }
      throw new AppError(error);
    });

/**
 * Erro da troca de e-mail com o `reason` e a etapa devolvidos pelo backend: é a etapa que diz à
 * tela para onde voltar (código do e-mail atual, do novo, ou recomeçar).
 */
export function EmailChangeError({ reason, stage, cooldownInSeconds } = {}) {
  this.name = 'EmailChangeError';
  this.reason = reason;
  this.stage = stage;
  this.cooldownInSeconds = cooldownInSeconds;
  this.message = reason || 'EMAIL_CHANGE_FAILED';
}

EmailChangeError.prototype = Object.create(Error.prototype);
EmailChangeError.prototype.constructor = EmailChangeError;

const postEmailChange = async (action, body) => {
  try {
    const { data } = await AuthenticatedHttp.post(`/user/email-change/${action}/`, body);
    return data;
  } catch (error) {
    const data = error?.response?.data ?? {};
    throw new EmailChangeError({
      reason: data.reason,
      stage: data.stage,
      cooldownInSeconds: data.cooldown_in_seconds,
    });
  }
};

export const requestEmailChange = (newEmail) => postEmailChange('request', { new_email: newEmail });

export const resendEmailChangeCode = () => postEmailChange('resend');

export const verifyEmailChangeCode = (otpCode) => postEmailChange('verify', { otp_code: otpCode });
